import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Upload, Trash2, Loader2, ImageOff, X, Lock, Play } from "lucide-react";
import { API_BASE, TOKEN_KEY } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const mediaUrl = (m) => `${API_BASE}/media/file/${m.id}?token=${m.token}`;

export function DamagePhotos({ jobId }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [gated, setGated] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/media?job_id=${jobId}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (res.ok) setMedia(await res.json());
    } finally { setLoading(false); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file, caption = "") => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "capture.jpg");
      const res = await fetch(`${API_BASE}/media/upload?job_id=${jobId}&caption=${encodeURIComponent(caption)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: fd,
      });
      if (res.status === 403) { const b = await res.json().catch(() => ({})); setGated(b.detail?.message || b.detail || "Damage photos are a Pro feature."); return; }
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.detail || `Upload failed (${res.status})`); }
      toast.success("Photo added"); setGated(null); load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      const res = await fetch(`${API_BASE}/media/${mid}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (!res.ok) throw new Error("Delete failed");
      setMedia((p) => p.filter((m) => m.id !== mid));
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="damage-photos">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase text-midnight">Damage Photos</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setCamOpen(true)} disabled={uploading} data-testid="damage-camera-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-bold text-midnight hover:bg-secondary disabled:opacity-60"><Camera className="h-4 w-4" />Camera</button>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="damage-upload-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload</button>
          <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>

      {gated ? (
        <div className="flex flex-col items-center gap-2 rounded-lg bg-warning-bg p-6 text-center" data-testid="damage-gated">
          <Lock className="h-6 w-6 text-warning" />
          <p className="text-sm font-semibold text-warning">{gated}</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : media.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-8 text-center">
          <ImageOff className="h-6 w-6 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">No photos yet. Capture damage on arrival or during the job.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {media.map((m) => (
            <div key={m.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-secondary" data-testid={`damage-item-${m.id}`}>
              {m.kind === "video" ? (
                <video src={mediaUrl(m)} className="h-full w-full object-cover" muted playsInline />
              ) : (
                <img src={mediaUrl(m)} alt={m.caption || "damage"} className="h-full w-full object-cover" />
              )}
              {m.kind === "video" && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><Play className="h-6 w-6 text-white drop-shadow" /></div>}
              <button onClick={() => remove(m.id)} data-testid={`damage-delete-${m.id}`} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <CameraModal open={camOpen} onClose={() => setCamOpen(false)} onCapture={(blob) => { setCamOpen(false); upload(new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" })); }} />
    </div>
  );
}

function CameraModal({ open, onClose, onCapture }) {
  const videoRef = useRef();
  const streamRef = useRef();
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setError(null); setReady(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setReady(true);
      } catch (e) { setError(e.name === "NotAllowedError" ? "Camera permission denied. Allow camera access in your browser to capture photos." : "No camera available on this device. Use Upload instead."); }
    })();
    return () => { active = false; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [open]);

  const snap = () => {
    const v = videoRef.current; if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob((blob) => blob && onCapture(blob), "image/jpeg", 0.9);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle className="font-display uppercase">Capture Damage Photo</DialogTitle></DialogHeader>
        {error ? (
          <div className="rounded-lg bg-danger-bg p-6 text-center text-sm font-semibold text-danger" data-testid="camera-error">{error}</div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg bg-black">
              <video ref={videoRef} className="w-full" muted playsInline autoPlay />
            </div>
            <button onClick={snap} disabled={!ready} data-testid="camera-snap-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
              {ready ? <><Camera className="h-4 w-4" />CAPTURE</> : <><Loader2 className="h-4 w-4 animate-spin" />Starting camera…</>}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
